"use client";

import { useEffect, useRef, useState } from "react";
import { useConsent } from "@/lib/consent/hooks";
import { useLegitimateInterest } from "@/lib/consent/region-context";
import { scheduleWhenIdle } from "@/lib/schedule-when-idle";

/** The frame's own document; same origin, so `frame-src 'self'` already covers it. */
const FRAME_SRC = "/api/ads/adsterra-frame";

/**
 * How long the frame gets to report before the slot gives up and hands the
 * space back to the first-party CTA. The frame reports its own verdict at 3s,
 * so this only bites when the frame never loads at all.
 */
const FILL_TIMEOUT_MS = 4000;

/** What the frame posts up. Anything that fails this shape is ignored. */
type FrameMessage = {
	source: "ld-adsterra";
	filled: boolean;
	height: number;
	blockedHosts: string[];
};

function parseFrameMessage(data: unknown): FrameMessage | null {
	if (typeof data !== "object" || data === null) return null;
	const m = data as Partial<FrameMessage>;
	if (m.source !== "ld-adsterra" || typeof m.filled !== "boolean") return null;
	return {
		source: "ld-adsterra",
		filled: m.filled,
		height: typeof m.height === "number" ? m.height : 0,
		blockedHosts: Array.isArray(m.blockedHosts)
			? m.blockedHosts.filter((h): h is string => typeof h === "string")
			: [],
	};
}

/**
 * Renders one Adsterra Native Banner inside a sandboxed same-origin frame.
 *
 * Four deliberate choices:
 *
 * 1. **A frame of our own, not an inline script.** Adsterra rotates the domains
 *    its loader fetches from, so running it on the page meant widening the site
 *    CSP on every rotation — and a host we had not allowlisted produced a
 *    silently blank slot, which is exactly the bug this replaced. The loader now
 *    lives in `/api/ads/adsterra-frame`, which carries its own ad-scoped policy.
 *    The page policy names no ad domain at all.
 *
 * 2. **No `sandbox` attribute at all — and this is the line that decides whether
 *    an ad ever renders.** Two earlier theories were both wrong, and both were
 *    shipped: #4874 sandboxed WITHOUT `allow-same-origin` (opaque origin →
 *    `window.top.document` throws → loader dies), and #4916 added
 *    `allow-same-origin` believing that fixed it. It did not. Retested in a real
 *    browser on 2026-09-05, the SAME page, interleaved to average out per-request
 *    rotation:
 *
 *        sandbox="allow-same-origin allow-scripts allow-popups
 *                 allow-popups-to-escape-sandbox"     → 0 fills / 6 attempts
 *        every sandbox flag there is (incl. forms,
 *                 modals, top-nav, storage-access)     → 0 fills / 3 attempts
 *        no sandbox attribute                          → fills (2/6, and 4/4
 *                                                        top-level)
 *
 *    So it is not a missing flag — it is the presence of the attribute. With
 *    `allow-same-origin` the loader can read `window.frameElement.sandbox`, and a
 *    non-empty value there is a bail signal for its anti-fraud; an unsandboxed
 *    frame reports an empty one and proceeds. The fill rate is low regardless
 *    (Adsterra is a low-value fallback), but sandboxed has never filled once and
 *    unsandboxed does.
 *
 *    The containment that remains is real and is what we actually rely on: this
 *    is OUR document, served from `/api/ads/adsterra-frame` with its own
 *    `default-src 'none'` CSP that bounds every host the ad script can reach
 *    (`frame-ancestors 'self'`, `form-action 'none'`, `base-uri 'none'`). What
 *    dropping the sandbox gives up is the last belt-and-suspenders layer —
 *    notably it no longer blocks the ad script from a top-level navigation — a
 *    real residual risk accepted deliberately: a same-origin ad script we chose
 *    to run cannot be meaningfully sandboxed AND allowed to serve, and the ad is
 *    a fallback on inventory worth cents. If that risk stops being acceptable the
 *    answer is a dedicated `ads.` origin, not the sandbox that never worked.
 *
 * 3. **Consent-gated, same rule as the other marketing scripts.** An explicit marketing-consent
 *    grant wins everywhere; with no answer recorded yet, the unit loads only
 *    outside EU/EEA/UK/CH via legitimate interest, and fails closed until
 *    `/api/privacy-region` resolves. Ads are marketing, not functional.
 *
 * 4. **Scheduled off the critical path** via `scheduleWhenIdle`. A third-party
 *    widget's unscheduled 32-request cascade once pushed a job page's apply CTA
 *    to ~49s (see `schedule-when-idle.ts`); an ad network gets the same treatment. The slot
 *    reserves its height from `SLOT_DIMENSIONS` regardless, so a late fill
 *    cannot shift layout.
 *
 * There is deliberately no `unit` prop: the frame route resolves the placement
 * from the same build-time env a caller would have read, so the key never has to
 * travel through the client. `AdSlotClient` still checks a unit is configured
 * before rendering this at all.
 *
 * `onResolved` fires exactly once with whether a creative actually rendered, so
 * `AdSlotClient` can fall back to the first-party CTA instead of leaving a
 * reserved hole. Every path that ends in "no ad" reports it: consent withheld,
 * the frame reporting a no-fill, or the frame never reporting at all.
 */
export function AdsterraNative({
	onResolved,
	onCspBlocked,
}: {
	/** Fired at most once: `true` when a creative rendered, `false` on any no-fill. */
	onResolved: (filled: boolean) => void;
	/** Hosts the frame's own CSP blocked — the rotation signal. */
	onCspBlocked?: (hosts: string[]) => void;
}) {
	const { record, hydrated } = useConsent();
	const legitimateInterestOk = useLegitimateInterest();
	const frameRef = useRef<HTMLIFrameElement>(null);
	const [mounted, setMounted] = useState(false);

	// Held in refs so a caller passing inline closures cannot remount the frame.
	const onResolvedRef = useRef(onResolved);
	onResolvedRef.current = onResolved;
	const onCspBlockedRef = useRef(onCspBlocked);
	onCspBlockedRef.current = onCspBlocked;

	const allowed =
		hydrated &&
		(record
			? record.choices.marketing === true
			: legitimateInterestOk === true);

	// Consent is still being decided while the store has not hydrated, or while
	// there is no stored record and `/api/privacy-region` has not landed. Hold
	// the slot for that moment rather than declaring a no-fill: reporting `false`
	// here would paint the house CTA and then swap it for an ad a beat later.
	const resolving =
		!hydrated || (record === null && legitimateInterestOk === null);

	useEffect(() => {
		if (resolving) return;
		// A visitor who declined marketing consent gets the first-party CTA, not a
		// blank reserved box. This is the common case, not an edge one — the cookie
		// banner's "reject" button writes `ONLY_NECESSARY`, i.e. `marketing: false`.
		if (!allowed) {
			onResolvedRef.current(false);
			return;
		}
		return scheduleWhenIdle(() => setMounted(true));
	}, [resolving, allowed]);

	useEffect(() => {
		if (!mounted) return;
		let settled = false;
		const settle = (filled: boolean) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			onResolvedRef.current(filled);
		};

		const onMessage = (event: MessageEvent) => {
			// Identity comes from the window handle, not `event.origin`: only OUR
			// frame can be its own `contentWindow`, and an origin string would also
			// match any other same-origin frame on the page.
			if (event.source !== frameRef.current?.contentWindow) return;
			const message = parseFrameMessage(event.data);
			if (!message) return;
			if (message.blockedHosts.length > 0) {
				onCspBlockedRef.current?.(message.blockedHosts);
			}
			settle(message.filled);
		};

		const timer = setTimeout(() => settle(false), FILL_TIMEOUT_MS);
		window.addEventListener("message", onMessage);
		return () => {
			window.removeEventListener("message", onMessage);
			clearTimeout(timer);
		};
	}, [mounted]);

	if (!mounted) return null;

	return (
		<iframe
			ref={frameRef}
			src={FRAME_SRC}
			title="Sponsored"
			// Deliberately NO `sandbox` attribute. It is not an oversight and not a
			// safe default here: any sandbox value (even every flag) makes the
			// loader bail and the ad never renders (proven 2026-09-05 — see the
			// component doc comment). Containment is the frame document's own
			// `default-src 'none'` CSP, not the sandbox. Do not re-add it.
			// No `loading="lazy"`: this element only exists once the guardrail,
			// consent and idle gates have all cleared, so it is already the last
			// thing to mount. A second, browser-timed deferral on top of that
			// pushed the fill (and the 4 s no-fill fallback behind it) past the
			// moment the reader scrolled by.
			scrolling="no"
			className="block w-full border-0"
			style={{ height: "100%", minHeight: "inherit" }}
		/>
	);
}
